import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from 'lucide-react';

const CONTACT_INFO = [
  { icon: Mail, title: 'Email', content: 'Tauqeer248@gmail.com', href: 'mailto:Tauqeer248@gmail.com' },
  { icon: Phone, title: 'Phone', content: '+49 15510793298', href: 'tel:+4915510793298' },
  { icon: MapPin, title: 'Location', content: 'Bamberg, Germany', href: null },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email address';

    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    // NOTE: this simulates a send. Wire up @emailjs/browser (already installed)
    // or a backend endpoint here to actually deliver messages.
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 4000);
    }, 1400);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const fieldClass = (hasError) =>
    `w-full px-4 py-3 rounded-lg bg-white/[0.03] border text-white placeholder-white/30
     focus:outline-none focus:ring-2 focus:ring-mint-400/50 focus:border-transparent
     transition-colors ${hasError ? 'border-red-400/60' : 'border-white/10'}`;

  return (
    <section id="contact" className="relative py-20 sm:py-28 bg-ink-950 overflow-hidden font-body">
      <div className="absolute inset-0 bg-grain pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-64 bg-mint-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="section-eyebrow">Contact</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold font-display">
            <span className="text-gradient">Let's build something</span>
          </h2>
          <p className="mt-4 text-white/50 max-w-xl mx-auto">
            Got a project, a role, or just want to say hi? I usually reply within a day.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {CONTACT_INFO.map((item, i) => (
            <motion.div
              key={item.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="glass glass-hover rounded-2xl p-5"
            >
              <div className="flex items-center gap-2 mb-2 text-mint-400">
                <item.icon className="w-4 h-4" />
                <h3 className="text-sm font-semibold text-white">{item.title}</h3>
              </div>
              {item.href ? (
                <a href={item.href} className="text-white/60 hover:text-mint-300 transition-colors text-sm">
                  {item.content}
                </a>
              ) : (
                <p className="text-white/60 text-sm">{item.content}</p>
              )}
            </motion.div>
          ))}
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass rounded-2xl p-6 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-sm text-white/60 mb-2">
                Your name
              </label>
              <input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={fieldClass(errors.name)}
                placeholder="John Doe"
              />
              {errors.name && <p className="text-red-400 text-xs mt-1.5">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm text-white/60 mb-2">
                Your email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={fieldClass(errors.email)}
                placeholder="john@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm text-white/60 mb-2">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              className={`${fieldClass(errors.message)} resize-none`}
              placeholder="Tell me about your project..."
            />
            {errors.message && <p className="text-red-400 text-xs mt-1.5">{errors.message}</p>}
          </div>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="interactive w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-ink-950 bg-gradient-to-r from-mint-400 to-cyan-400 disabled:opacity-60 transition-opacity"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : submitted ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Sent — thank you!
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send message
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactSection;
